import { useEffect, useState } from 'react';

type Listener = () => void;

type TickerBucket = {
  listeners: Set<Listener>;
  intervalId: ReturnType<typeof setInterval> | null;
};

const tickerBuckets = new Map<number, TickerBucket>();
const DEFAULT_TICK_INTERVAL_MS = 10000;
const MIN_TICK_INTERVAL_MS = 1000;

function resolveIntervalMs(intervalMs?: number) {
  if (!intervalMs || Number.isNaN(intervalMs) || intervalMs < MIN_TICK_INTERVAL_MS) {
    return DEFAULT_TICK_INTERVAL_MS;
  }

  return Math.floor(intervalMs);
}

function getBucket(intervalMs: number) {
  const existing = tickerBuckets.get(intervalMs);
  if (existing) {
    return existing;
  }

  const created: TickerBucket = { listeners: new Set(), intervalId: null };
  tickerBuckets.set(intervalMs, created);
  return created;
}

function startTicker(intervalMs: number, bucket: TickerBucket) {
  if (!bucket.intervalId) {
    bucket.intervalId = setInterval(() => {
      bucket.listeners.forEach(listener => listener());
    }, intervalMs);
  }
}

function stopTicker(intervalMs: number, bucket: TickerBucket) {
  if (bucket.listeners.size === 0 && bucket.intervalId) {
    clearInterval(bucket.intervalId);
    tickerBuckets.delete(intervalMs);
  }
}

export function useSharedTicker(intervalMs?: number) {
  const [tick, setTick] = useState(Date.now());
  const resolvedIntervalMs = resolveIntervalMs(intervalMs);

  useEffect(() => {
    const update = () => setTick(Date.now());
    const bucket = getBucket(resolvedIntervalMs);

    bucket.listeners.add(update);
    startTicker(resolvedIntervalMs, bucket);

    return () => {
      bucket.listeners.delete(update);
      stopTicker(resolvedIntervalMs, bucket);
    };
  }, [resolvedIntervalMs]);

  return tick;
}
